import { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../components/Button/Button';
import LoaderInline from '../../../components/Loader/LoaderInline';
import generateUserAgreement from '../../../api/creditPlatform/generateUserAgreement';
import DocsInputNew from '../../../components/Inputs/DocsInput/DocsInputNew';
import { textStyles } from '../../../constants/textStyles';
import uploadDocumentToDeal from '../../../api/deals/uploadDocumentToDeal';
import { DOCUMENT_STATUS_SIGNED, DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT } from '../../../constants';
import { socket } from '../../../App';
import generateLink from '../../../api/signature/generateLink';
import alert from '../../../helpers/alert';
import { humanErrorText } from '../../../helpers/errorText';
import extractErrorMessage from '../../../utils/extractErrorMessage';
import phoneBelongsIinValidate from '../../../api/bmg/phoneBelongsIinValidate';
import getClients from '../../../api/clients/getClients';
import { loadFreshClient, pickClientContact, BMG_UNAVAILABLE_MESSAGE } from '../../../utils/clientContact';
import { SIGN_TYPES } from '../../Public/SignDocuments';
import userSettings from '../../../api/clients/userSettings';

const SignatureType = ({ pickMobile, pickUpload, sendSmsForSign }) => {
  return (
    <>
      <p
        style={{
          ...textStyles.formTitle,
          marginBottom: 20,
          marginTop: 20,
        }}
      >
        Для формирования заявок на кредит клиент должен подписать согласие на обработку персональных данных
      </p>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <Button text="Отправить клиенту согласие на подписание в мобильном приложении" fill onClick={pickMobile} />
        <Button text="Подписать и загрузить согласие" fill onClick={pickUpload} />
        <Button text="Отправить СМС со ссылкой на подписание ЭЦП" fill onClick={() => sendSmsForSign(false)} />
        <Button text="Отправить СМС со ссылкой на подписание через OTP" fill onClick={() => sendSmsForSign(true)} />
      </div>
    </>
  );
};

const SigningLink = ({ link }) => {
  if (!link) return null;

  const copy = () => {
    navigator.clipboard.writeText(link).then(
      () => alert.success('Ссылка скопирована'),
      () => alert.error('Не удалось скопировать — выделите ссылку и скопируйте вручную')
    );
  };

  return (
    <div style={{ marginTop: 20, padding: 14, background: '#f7f7f8', borderRadius: 10 }}>
      <p style={{ fontSize: 14, color: '#6b6b70', margin: '0 0 8px' }}>
        Ссылка, отправленная клиенту. Если СМС не дошла — продиктуйте или перешлите её сами.
      </p>
      <p style={{ fontSize: 14, wordBreak: 'break-all', margin: '0 0 10px' }}>{link}</p>
      <Button text="Скопировать ссылку" onClick={copy} />
    </div>
  );
};

const SendAgreement = ({ onDocumentSigned, loading, signingLink }) => {
  // Кнопка вызывала отправку напрямую, минуя защиту от повтора: менеджер жал её,
  // а параллельно приходило событие о подписании — в банки уходили две заявки.
  const [pressed, setPressed] = useState(false);
  // Своей подписки здесь нет намеренно. Раньше экран слушал канал документа, а
  // корень модалки — канал сделки, и у каждого была своя защита от повтора:
  // сервер шлёт событие в оба, срабатывали оба, и в банки уходили дубли заявок
  // (сделка 189953 на проде). Отправку делает только корень модалки.
  if (loading) return <LoaderInline />;

  return (
    <>
      <div
        style={{
          margin: 30,
        }}
      >
        <LoaderInline />
      </div>
      <p style={{ ...textStyles.formTitle, textAlign: 'center' }}>
        Ожидается подписание согласия на обработку персональных данных клиентом
      </p>

      <div
        style={{
          marginTop: 20,
        }}
      >
        <Button
          onClick={() => {
            if (pressed) return;
            setPressed(true);
            onDocumentSigned();
          }}
          disabled={pressed}
          text="Отправить заявку на кредит"
          fill
          full
        />
      </div>

      <SigningLink link={signingLink} />
    </>
  );
};
const UploadAgreement = ({ deal, sendAppeal, agreementFile, onClose }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = () => {
    setLoading(true);
    uploadDocumentToDeal({
      dealId: deal?.id,
      statusId: DOCUMENT_STATUS_SIGNED,
      fileTypeId: DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT,
      file,
      signed: true,
    })
      .then(() => {
        sendAppeal().finally(() => setLoading(false));
      })
      .catch(() => {
        setLoading(false);
      });
  };

  if (!agreementFile || loading) return <LoaderInline />;

  return (
    <>
      <p style={{ ...textStyles.formTitle, textAlign: 'center', margin: '20px 0 40px 0' }}>
        Подписание согласия на обработку персональных данных клиентом
      </p>
      <div
        style={{
          marginBottom: 20,
        }}
      >
        <div style={{ marginBottom: 20, marginTop: 20, display: 'flex', justifyContent: 'center' }}>
          <a
            href={agreementFile}
            target="_blank"
            style={{
              gap: 7,
              display: 'inline-grid',
              gridTemplateColumns: 'auto 1fr',
              alignItems: 'center',
              marginBottom: 5,
            }}
            rel="noreferrer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
              <path
                d="M2 16H18V9H20V17C20 17.2652 19.8946 17.5196 19.7071 17.7071C19.5196 17.8946 19.2652 18 19 18H1C0.734784 18 0.48043 17.8946 0.292893 17.7071C0.105357 17.5196 0 17.2652 0 17V9H2V16ZM12 6H17L10 13L3 6H8V0H12V6Z"
                fill="var(--brand-primary)"
              />
            </svg>
            <a style={{ color: 'var(--brand-primary)', fontSize: 16, fontWeight: 400 }}>
              Скачать согласие на обработку персональных данных
            </a>
          </a>
        </div>
        <DocsInputNew
          id="agreement"
          label={file?.name || 'Загрузить подписанное клиентом согласие на обработку персональных данных'}
          onChange={setFile}
        />
      </div>
      <div
        style={{
          display: 'flex',
          gap: 15,
        }}
      >
        <Button text="Отправить" fill disabled={!file || loading} onClick={onSubmit} />
        <Button
          text="Назад"
          disabled={loading}
          onClick={onClose}
          style={{ color: 'var(--brand-primary)', border: '1px solid var(--brand-primary)' }}
        />
      </div>
    </>
  );
};

const DealCreateAgreement = ({ deal, sendAppeal, onClose, sendFormLoading, bankIds, creditAppeal }) => {
  const [step, setStep] = useState(1);
  const [agreementDocumentId, setAgreementDocumentId] = useState(null);
  // Ссылка, выданная клиенту. Из ответа сервера — на текущей странице, из сделки —
  // после перезагрузки: там она сохранена.
  const [signingLink, setSigningLink] = useState(null);
  const [loading, setLoading] = useState(false);

  // Подписка на канал сделки живёт в корне модалки, а не на экране ожидания:
  // тот рисуется только на своём шаге, и после перезагрузки страницы шаг
  // сбрасывается — подписки не было вовсе, и подписанная клиентом заявка не
  // уходила (сделки 189939 и 189942 на проде).
  const appealSentRef = useRef(false);
  useEffect(() => {
    if (!deal?.id) return undefined;
    const channel = 'listenSignatureInDeal' + deal.id;
    socket.on(channel, (data) => {
      if (data && data.success === false) {
        alert.error(
          data.message || 'Клиент не завершил подписание. Можно отправить ссылку заново',
          { autoClose: false, closeOnClick: false }
        );
        return;
      }
      // Отправляем один раз: событие приходит и по каналу документа тоже, а
      // двойной вызов создал бы дубли заявок в банках.
      if (appealSentRef.current) return;
      appealSentRef.current = true;
      sendAppeal();
    });
    return () => {
      socket.off(channel);
    };
  }, [deal?.id, sendAppeal]);

  console.log('bankIds', bankIds);

  const pickMobile = () => {
    setLoading(true);
    generateUserAgreement({ userid: deal?.client?.id, chunkId: deal?.chunkId, bankIds, creditAppeal })
      .then((res) => {
        setAgreementDocumentId(res?.data?.documentId);
        setStep('mobile');
      })
      .catch((err) => {
        if (!humanErrorText(err?.response?.data)) {
          alert.error('Не удалось сгенерировать согласие на обработку персональных данных');
        }
      })
      .finally(() => setLoading(false));
  };

  const [agreementFile, setAgreementFile] = useState(null);
  const pickUpload = () => {
    generateUserAgreement({ userid: deal?.client?.id, chunkId: deal?.chunkId, bankIds, creditAppeal })
      .then((res) => {
        if (!res?.data?.url) {
          alert('Не удалось сгенерировать согласие на обработку персональных данных');
          return;
        }
        setAgreementFile(res?.data?.url);
        window.open(res?.data?.url, '_blank');

        setStep('upload');
      })
      .catch((err) => {
        if (!humanErrorText(err?.response?.data)) {
          alert.error(
            extractErrorMessage(err?.response?.data) ||
              'Не удалось сгенерировать согласие на обработку персональных данных'
          );
        }
      });
  };

  const onDocumentSigned = () => {
    sendAppeal();
  };

  const sendSmsForSign = async (otp) => {
    setLoading(true);

    try {
      // Клиента перечитываем с сервера: страница сделки помнит телефон с момента
      // открытия, а менеджер мог сменить его в карточке (сделка 191883).
      const freshClient = await loadFreshClient(getClients, deal?.client?.id);
      const isPhoneBelongsToClient = await phoneBelongsIinValidate(
        pickClientContact(freshClient, deal?.client)
      );

      if (!isPhoneBelongsToClient) {
        alert.error('Клиент с этим ИИН не является владельцем введенного номера телефона');
        setLoading(false);
        return;
      }
    } catch (e) {
      alert.error(BMG_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }

    generateUserAgreement({ userid: deal?.client?.id, chunkId: deal?.chunkId, bankIds, creditAppeal })
      .then((res) => {
        // Клиент уже подписывает: бэкенд не стал пересоздавать пакет, а продублировал
        // клиенту прежнюю ссылку. Новую выпускать нельзя — она обнулит подписание.
        if (res?.data?.resent) {
          alert.success(res?.data?.message || 'Ссылка на подписание отправлена клиенту повторно');
          if (res?.data?.link) setSigningLink(res.data.link);
          // Сообщение обещает ссылку «ниже», а она живёт на экране ожидания —
          // на экране с кнопками её нет. Переводим туда, иначе подсказка врёт.
          setStep('mobile');
          setLoading(false);
          return;
        }
        if (!res?.data?.url) {
          alert.error(
            'Не удалось сгенерировать согласие на обработку персональных данных. Отсутствует ссылка на документ'
          );
          setLoading(false);
          return;
        }
        setAgreementDocumentId(res?.data?.documentId?.[0]);
        generateLink({
          url: res?.data?.url,
          name: 'docs',
          typeId: DOCUMENT_TYPES_PERSONAL_DATA_AGREEMENT,
          documentId: res?.data?.documentId?.[0],
          userId: deal?.client?.id,
          dealId: deal?.id,
          signType: otp ? SIGN_TYPES.OTP : SIGN_TYPES.ECP,
          bankIds,
        })
          .then((linkRes) => {
            if (linkRes?.data?.link) setSigningLink(linkRes.data.link);
            alert.success('СМС успешно отправлено');
            setStep('mobile');
          })
          .catch(() => {
            alert.error('Ошибка отправки СМС');
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch((err) => {
        // Понятную причину уже показал перехватчик в api.js — свой общий текст
        // добавляем только тогда, когда сервер ничего не объяснил. Иначе
        // менеджер получает два тоста подряд.
        if (!humanErrorText(err?.response?.data)) {
          alert.error('Не удалось сгенерировать согласие на обработку персональных данных');
        }
        setLoading(false);
      });
  };

  if (loading) {
    return <LoaderInline />;
  }

  return (
    <>
      {step === 1 && <SignatureType pickMobile={pickMobile} pickUpload={pickUpload} sendSmsForSign={sendSmsForSign} />}
      {step === 'mobile' && (
        <SendAgreement
          onDocumentSigned={onDocumentSigned}
          loading={sendFormLoading}
          signingLink={signingLink || deal?.signingLink}
        />
      )}
      {step === 'upload' && (
        <UploadAgreement
          setStep={setStep}
          deal={deal}
          sendAppeal={sendAppeal}
          agreementFile={agreementFile}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default DealCreateAgreement;

